import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { useEffect, useState, useRef, useCallback } from "react";
import ExpandableTextMobile from "../components/ExpandableText";
import { Modal, Button } from "react-bootstrap";
import { notifySuccess, notifyError } from "../components/notify";
import { BASE_MOBILE_API_URL, BASIC_AUTH } from "../constants";
import axios from "axios";
import TableLoader from "../components/Spinner/TableLoader";
import ButtonSpinner from "../components/Spinner/ButtonSpinner";

const NewOrderAddressList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setPageTitle } = useOutletContext();

  useEffect(() => {
    setPageTitle("Address List");
  }, [setPageTitle]);

  const receivedPayload = location.state?.finalData;
  const [payload, setPayload] = useState(receivedPayload);
  const [showSlotModal, setShowSlotModal] = useState(false);
  const [showConfirmationModal, setShowConfimationModal] = useState(false);
  const [address, setAddress] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState("");

  // ── Add Address State ──
  const [showAddAddress, setAddAddressModal] = useState(false);
  const [addAddressLoading, setAddAddressLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isActive, setIsActive] = useState(true);
  const [formData, setFormData] = useState({
    addressName: "",
    addressline1: "",
    addressline2: "",
    landmark: "",
    state: "",
    city: "",
    pincode: "",
    addressLocation: "",
    addressType: "",
  });

  // ── Refs for Add Address form ──
  const nameRef = useRef();
  const address1Ref = useRef();
  const stateRef = useRef();
  const cityRef = useRef();
  const pincodeRef = useRef();
  const locationRef = useRef();
  const addressTypeRef = useRef();

  const customerStr = localStorage.getItem("customerInfo");
  const customer = customerStr ? JSON.parse(customerStr) : null;

  const timeslotStr = localStorage.getItem("deliveryTimeSlot");
  const timeSlots = timeslotStr ? JSON.parse(timeslotStr) : [];

  // ── Focus address name when modal opens ──
  useEffect(() => {
    if (showAddAddress && nameRef.current) {
      nameRef.current.focus();
    }
  }, [showAddAddress]);

  // ── Fetch Address List ──
  const getAddressList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${BASE_MOBILE_API_URL}/get_customer_address`,
        {
          params: {
            companycode: customer?.companycode,
            supervisorcode: "0",
            branchcode: customer?.branchcode,
            customercode: customer?.customercode,
          },
          headers: { Authorization: BASIC_AUTH },
        }
      );
      setAddress(response.data);
    } catch (err) {
      if (err.response) {
        notifyError(
          err.response?.data && typeof err.response.data === "string"
            ? err.response.data
            : err.response?.data?.message || err.response?.data?.error || "Fetch address list failed"
        );
      } else if (err.request) {
        notifyError("Server not responding. Please try again.");
      } else {
        notifyError("Something went wrong.");
      }
    } finally {
      setLoading(false);
    }
  }, [customer]);

  const hasFetched = useRef(false);
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    getAddressList();
  }, [getAddressList]);

  // ── Select Address & Build Payload ──
  const handleSelectAddress = () => {
    setPayload((prev) => ({
      ...prev,
      header: {
        ...prev.header,
        delivery_address_text: [
          selectedAddress.address_line1,
          selectedAddress.address_line2,
          selectedAddress.city,
          selectedAddress.state,
        ].filter(Boolean).join(", "),
        delivery_gps_location: selectedAddress.gps_location,
        delivery_pincode: selectedAddress.pincode,
        delivery_time_slot: selectedSlot,
        delivery_distance_val: selectedAddress.delivery_distance,
      },
    }));
  };

  // ── Create Order ──
  const handleCreateOrder = async () => {
    setAddLoading(true);
    try {
      await axios.post(`${BASE_MOBILE_API_URL}/post_customer_order`, payload, {
        headers: { Authorization: BASIC_AUTH },
      });
      setShowConfimationModal(false);
      setSelectedSlot("");
      setSelectedAddress(null);
      localStorage.removeItem("orderItems");
      notifySuccess("Order created successfully!");
      navigate("/new_order");
    } catch (err) {
      if (err.response) {
        notifyError(
          err.response?.data && typeof err.response.data === "string"
            ? err.response.data
            : err.response?.data?.message || err.response?.data?.error || "Create order failed"
        );
      } else if (err.request) {
        notifyError("Server not responding. Please try again.");
      } else {
        notifyError("Something went wrong.");
      }
    } finally {
      setAddLoading(false);
    }
  };

  // ── Add Address: Helpers ──
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setFormData((prev) => ({ ...prev, addressLocation: "0,0" }));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setFormData((prev) => ({ ...prev, addressLocation: `${latitude},${longitude}` }));
      },
      () => {
        setFormData((prev) => ({ ...prev, addressLocation: "0,0" }));
      }
    );
  };

  const resetForm = () => {
    setFormData({
      addressName: "", addressline1: "", addressline2: "",
      landmark: "", state: "", city: "",
      pincode: "", addressLocation: "", addressType: "",
    });
    setIsActive(true);
    setErrors({});
  };

  const openAddModal = () => {
    resetForm();
    setAddAddressModal(true);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.addressName.trim())     newErrors.addressName     = "Address Name is required";
    if (!formData.addressline1.trim())    newErrors.addressline1    = "Address Line 1 is required";
    if (!formData.state.trim())           newErrors.state           = "State is required";
    if (!formData.city.trim())            newErrors.city            = "City is required";
    if (!formData.pincode.trim())         newErrors.pincode         = "Pincode is required";
    if (!formData.addressLocation.trim()) newErrors.addressLocation = "Location is required";
    if (!formData.addressType.trim())     newErrors.addressType     = "Address Type is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      if (newErrors.addressName)          nameRef.current?.focus();
      else if (newErrors.addressline1)    address1Ref.current?.focus();
      else if (newErrors.state)           stateRef.current?.focus();
      else if (newErrors.city)            cityRef.current?.focus();
      else if (newErrors.pincode)         pincodeRef.current?.focus();
      else if (newErrors.addressLocation) locationRef.current?.focus();
      else if (newErrors.addressType)     addressTypeRef.current?.focus();
      return false;
    }
    return true;
  };

  // ── Add Address: Submit ──
  const handleAddAddress = async () => {
    if (!validate()) return;

    setAddAddressLoading(true);
    try {
      const response = await axios.post(
        `${BASE_MOBILE_API_URL}/add_customer_address`,
        {
          companycode: customer?.companycode,
          branchcode: customer?.branchcode,
          customercode: customer?.customercode,
          address_type: formData.addressType,
          addressname: formData.addressName,
          address_line1: formData.addressline1,
          address_line2: formData.addressline2,
          landmark: formData.landmark,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode,
          gps_location: formData.addressLocation || "0,0",
          status: isActive ? "Y" : "N",
        },
        { headers: { Authorization: BASIC_AUTH, "Content-Type": "application/json" } }
      );

      if (response.status === 200 || response.status === 201) {
        notifySuccess(response.data?.message || "Address added successfully.");
        resetForm();
        setAddAddressModal(false);
        getAddressList(); // Refresh list
      }
    } catch (err) {
      if (err.response) {
        notifyError(
          err.response?.data && typeof err.response.data === "string"
            ? err.response.data
            : err.response?.data?.message || err.response?.data?.error || "Failed to add address"
        );
      } else if (err.request) {
        notifyError("Server not responding. Please try again.");
      } else {
        notifyError("Something went wrong.");
      }
    } finally {
      setAddAddressLoading(false);
    }
  };

  return (
    <>
      <div className="bg-black px-0">

        {/* ── Add New Address Button ── */}
        <div className="d-flex flex-column align-items-center justify-content-center mb-3 mt-2">
          <Button
            type="button"
            className="col-lg-2 col-md-3 col-7 btn-theme btn-md text-black"
            onClick={openAddModal}
          >
            <b>Add New Address</b>
          </Button>
        </div>

        {loading ? (
          <TableLoader
            message="Loading Address Data..."
            size="sm"
            messageClass="tablemessage-white"
            spinnerClass="tablespinner-white"
          />
        ) : (
          <div className="company-page">
            {address.map((i) => (
              <div className="row justify-content-center" key={i.addresscode}>
                <div className="col-12 col-md-6 col-lg-7">
                  <div
                    className="company-card"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setSelectedAddress(i);
                      setSelectedSlot("");
                      setShowSlotModal(true);
                    }}
                  >
                    <div className="company-right">
                      <div className="col-12 col-md-6 company-font-size">
                        <span className="company-label col-5">Address Name :</span>
                        <span className="company-value">{i.address_name}</span>
                      </div>

                      <div className="col-12 company-font-size">
                        <span className="company-label col-5">Address :</span>
                        <span className="company-value">
                          <ExpandableTextMobile
                            text={[i.address_line1, i.address_line2, i.city, i.state, i.pincode]
                              .filter(Boolean).join(", ")}
                            className="long-text"
                          />
                        </span>
                      </div>

                      <div className="col-12 company-font-size">
                        <span className="company-label col-5">Landmark :</span>
                        <span className="company-value">{i.landmark}</span>
                      </div>

                      <div className="col-12 company-font-size">
                        <span className="company-label col-5">Address Type :</span>
                        <span className="company-value">
                          {i.address_type === "H" ? "Home" : i.address_type === "O" ? "Office" : "Other"}
                        </span>
                      </div>

                      <div className="col-12 company-font-size">
                        <span className="company-label col-5">Address Location :</span>
                        <span className="company-value">
                          <ExpandableTextMobile
                            text={i.gps_location}
                            className="long-text mb-1 text-white"
                          />
                          {i.gps_location && (
                            <button
                              type="button"
                              className="map-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(`https://www.google.com/maps?q=${i.gps_location}`, "_blank");
                              }}
                            >
                              <i className="fa-solid fa-location-dot"></i>
                            </button>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {address.length === 0 && (
              <div className="text-center text-white mt-4">No addresses found.</div>
            )}
          </div>
        )}
      </div>

      {/* ── Slot Modal ── */}
      <Modal show={showSlotModal} onHide={() => setShowSlotModal(false)} backdrop="static" keyboard={false}>
        <div className="bg-Modal-black">
          <Modal.Header closeButton>
            <Modal.Title className="text-theme">Select Delivery Time Slot</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {selectedAddress && (
              <>
                <div className="mb-3">
                  <strong>Address Name:</strong> {selectedAddress.address_name}
                </div>
                <div className="mb-3">
                  <strong>Address:</strong>{" "}
                  <ExpandableTextMobile
                    text={[selectedAddress.address_line1, selectedAddress.address_line2,
                      selectedAddress.city, selectedAddress.state, selectedAddress.pincode]
                      .filter(Boolean).join(", ")}
                    className="long-text"
                  />
                  {selectedAddress.gps_location && (
                    <button
                      type="button"
                      className="map-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(`https://www.google.com/maps?q=${selectedAddress.gps_location}`, "_blank");
                      }}
                    >
                      <i className="fa-solid fa-location-dot"></i>
                    </button>
                  )}
                </div>
                <div className="mb-3">
                  <strong>Landmark:</strong> {selectedAddress.landmark}
                </div>
                <div className="mb-3">
                  <label className="form-label fw-bold">
                    Select Time Slot <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={selectedSlot}
                    onChange={(e) => setSelectedSlot(e.target.value)}
                  >
                    <option value="">Select Time Slot</option>
                    {timeSlots.map((slot) => (
                      <option key={slot.timeslotcode} value={slot.time_slot}>
                        {slot.time_slot}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button
              variant="success"
              onClick={() => {
                if (!selectedSlot) {
                  notifyError("Please select a delivery time slot.");
                  return;
                }
                handleSelectAddress();
                setShowSlotModal(false);
                setShowConfimationModal(true);
              }}
            >
              Add
            </Button>
          </Modal.Footer>
        </div>
      </Modal>

      {/* ── Confirm Order Modal ── */}
      <Modal show={showConfirmationModal} onHide={() => setShowConfimationModal(false)} backdrop="static" keyboard={false}>
        <div className="bg-Modal-black">
          <Modal.Header closeButton>
            <Modal.Title className="text-theme">Confirmation Alert</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Are you sure you want to submit order?</p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="danger" onClick={() => setShowConfimationModal(false)}>
              No
            </Button>
            <ButtonSpinner
              variant="success"
              className="btn btn-success"
              loading={addLoading}
              onClick={handleCreateOrder}
            >
              <b>{addLoading ? "Creating Order..." : "Yes"}</b>
            </ButtonSpinner>
          </Modal.Footer>
        </div>
      </Modal>

      {/* ── Add New Address Modal ── */}
      <Modal
        show={showAddAddress}
        onHide={() => { resetForm(); setAddAddressModal(false); }}
        size="lg"
        backdrop="static"
        keyboard={false}
      >
        <div className="bg-Modal-black">
          <Modal.Header className="py-2" closeButton>
            <Modal.Title className="text-theme p-1">Add New Address</Modal.Title>
          </Modal.Header>

          <Modal.Body className="px-3">
            {/* — Address Name & Type — */}
            <div className="d-md-flex d-lg-flex d-block gap-2">
              <div className="mb-3 col-md-6">
                <label className="form-label">Address Name <span className="red-error">*</span></label>
                <input
                  ref={nameRef} type="text" className="form-control"
                  name="addressName" value={formData.addressName}
                  onChange={handleChange} placeholder="Address Name"
                />
                {errors.addressName && <div className="text-danger small">{errors.addressName}</div>}
              </div>
              <div className="mb-3 col-md-6">
                <label className="form-label">Address Type <span className="red-error">*</span></label>
                <select
                  ref={addressTypeRef} className="form-select"
                  name="addressType" value={formData.addressType}
                  onChange={handleChange}
                >
                  <option value="">Select Type</option>
                  <option value="H">Home</option>
                  <option value="O">Office</option>
                  <option value="X">Other</option>
                </select>
                {errors.addressType && <div className="text-danger small">{errors.addressType}</div>}
              </div>
            </div>

            {/* — Address Line 1 & 2 — */}
            <div className="d-md-flex d-lg-flex d-block gap-2">
              <div className="mb-3 col-md-6">
                <label className="form-label">Address Line 1 <span className="red-error">*</span></label>
                <input
                  ref={address1Ref} type="text" className="form-control"
                  name="addressline1" value={formData.addressline1}
                  onChange={handleChange} placeholder="House / Flat No, Building Name, Street"
                />
                {errors.addressline1 && <div className="text-danger small">{errors.addressline1}</div>}
              </div>
              <div className="mb-3 col-md-6">
                <label className="form-label">Address Line 2</label>
                <input
                  type="text" className="form-control"
                  name="addressline2" value={formData.addressline2}
                  onChange={handleChange} placeholder="Area, Locality, Colony, Road (optional)"
                />
              </div>
            </div>

            {/* — Landmark & City — */}
            <div className="d-md-flex d-lg-flex d-block gap-2">
              <div className="mb-3 col-md-6">
                <label className="form-label">Landmark</label>
                <input
                  type="text" className="form-control"
                  name="landmark" value={formData.landmark}
                  onChange={handleChange} placeholder="Near temple, school, petrol pump (optional)"
                />
              </div>
              <div className="mb-3 col-md-6">
                <label className="form-label">City <span className="red-error">*</span></label>
                <input
                  ref={cityRef} type="text" className="form-control"
                  name="city" value={formData.city}
                  onChange={handleChange} placeholder="City / Town / Village"
                />
                {errors.city && <div className="text-danger small">{errors.city}</div>}
              </div>
            </div>

            {/* — State & Pincode — */}
            <div className="d-md-flex d-lg-flex d-block gap-2">
              <div className="mb-3 col-md-6">
                <label className="form-label">State <span className="red-error">*</span></label>
                <input
                  ref={stateRef} type="text" className="form-control"
                  name="state" value={formData.state}
                  onChange={handleChange} placeholder="State"
                />
                {errors.state && <div className="text-danger small">{errors.state}</div>}
              </div>
              <div className="mb-3 col-md-6">
                <label className="form-label">Pincode <span className="red-error">*</span></label>
                <input
                  ref={pincodeRef} type="number" className="form-control"
                  name="pincode" value={formData.pincode}
                  onChange={handleChange} placeholder="Pincode"
                />
                {errors.pincode && <div className="text-danger small">{errors.pincode}</div>}
              </div>
            </div>

            {/* — Location & Active Toggle — */}
            <div className="d-md-flex d-lg-flex d-block gap-2">
  <div className="mb-3 col-md-6">
    <div className="d-flex align-items-center gap-2 mb-1">
      <label className="form-label mb-0">
        Address Location <span className="red-error">*</span>
      </label>
      <button
        type="button"
        className="btn btn-sm btn-theme text-black"
        onClick={getCurrentLocation}
      >
        <i className="fa-solid fa-location-crosshairs me-1"></i>
        Get Location
      </button>
    </div>
    <input
      ref={locationRef}
      type="text"
      className="form-control"
      name="addressLocation"
      value={formData.addressLocation}
      onChange={(e) => setFormData({ ...formData, addressLocation: e.target.value })}
      placeholder="Enter or fetch GPS location"
    />
    {errors.addressLocation && (
      <div className="text-danger small">{errors.addressLocation}</div>
    )}
  </div>

  <div className="mb-3 col-md-6 d-flex align-items-center mt-4">
    <div className="form-check form-switch">
      <input
        className="form-check-input"
        type="checkbox"
        role="switch"
        id="activeSwitch"
        checked={isActive}
        onChange={(e) => setIsActive(e.target.checked)}
      />
      <label className="form-check-label" htmlFor="activeSwitch">
        {isActive ? "Active" : "Inactive"}
      </label>
    </div>
  </div>
</div>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="danger" onClick={() => { resetForm(); setAddAddressModal(false); }}>
              Cancel
            </Button>
            <ButtonSpinner
              type="button"
              variant="success"
              className="btn btn-success"
              loading={addAddressLoading}
              onClick={handleAddAddress}
            >
              {addAddressLoading ? "Submitting..." : "Submit"}
            </ButtonSpinner>
          </Modal.Footer>
        </div>
      </Modal>
    </>
  );
};

export default NewOrderAddressList;